import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { ShoppingBag, Eye, EyeOff, Lock, Mail, ArrowRight } from 'lucide-react';

export default function VendedorLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [storeLogo, setStoreLogo] = useState('');

  // Carrega logo público da loja
  useEffect(() => {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    axios.get(`${API}/public/settings`)
      .then(r => setStoreLogo(r.data?.logo_base64 || r.data?.logo || ''))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Preencha e-mail e senha');
      return;
    }
    setBusy(true);
    const result = await login(email.toLowerCase().trim(), password, { portal: 'vendedor' });
    setBusy(false);
    if (result.success) {
      const role = result.user?.role;
      if (result.mustChangePassword) {
        toast.success('Login OK! Por favor crie uma nova senha pessoal.');
        navigate('/vendedor/trocar-senha');
      } else if (role === 'vendedor') {
        navigate('/vendedor/dashboard');
      } else if (role === 'cliente') {
        navigate('/cliente/dashboard');
      } else {
        // segurança: nunca redirecionar pra admin daqui
        toast.error('Acesso negado a este portal');
        navigate('/login');
      }
    } else {
      toast.error(result.message || 'Falha no login');
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Informe o e-mail');
      return;
    }
    setBusy(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const axios = (await import('axios')).default;
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email: email.toLowerCase().trim() });
      toast.success('Se este e-mail existir, você receberá instruções em breve');
      setForgotMode(false);
    } catch (err) {
      toast.error('Erro ao solicitar recuperação');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
        {/* Cabeçalho colorido */}
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-orange-500 p-6 text-white text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-3 shadow-lg overflow-hidden p-1">
            {storeLogo ? (
              <img
                src={storeLogo}
                alt="Distribuidora Santa Ana"
                className="w-full h-full object-contain"
                data-testid="login-logo-img"
              />
            ) : (
              <ShoppingBag className="w-9 h-9 text-purple-600" />
            )}
          </div>
          <h1 className="text-2xl font-bold">Acesse sua Conta</h1>
          <p className="text-sm opacity-90 mt-1">Cliente ou Vendedor · Distribuidora Santa Ana</p>
        </div>

        {/* Formulário */}
        <form onSubmit={forgotMode ? handleForgot : handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1.5 flex items-center gap-1.5">
              <Mail className="w-3 h-3" /> E-mail
            </label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="seu@email.com"
              data-testid="vendedor-login-email"
              autoFocus
            />
          </div>

          {!forgotMode && (
            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1.5 flex items-center gap-1.5">
                <Lock className="w-3 h-3" /> Senha
              </label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 pr-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Sua senha"
                  data-testid="vendedor-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 transition"
            data-testid="vendedor-login-submit"
          >
            {busy ? 'Aguarde...' : (forgotMode ? 'Enviar recuperação' : 'Entrar')}
            <ArrowRight className="w-4 h-4" />
          </button>

          <div className="text-center text-xs">
            <button
              type="button"
              onClick={() => setForgotMode(m => !m)}
              className="text-blue-600 hover:underline"
            >
              {forgotMode ? '← Voltar ao login' : 'Esqueci minha senha'}
            </button>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 text-center text-xs text-gray-500">
            Não tem acesso? Solicite ao administrador da loja.
          </div>
        </form>
      </div>
    </div>
  );
}
